import { fetchConfig, debounce } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

/**
 * Free gift auto-add controller.
 *
 * Headless `<free-gift>` element that keeps a free gift in the cart once the
 * subtotal reaches a threshold. The gift is a normally-priced product made free
 * by an automatic Shopify discount ("Spend €X, get Y free"). The theme adds the
 * product; the discount zeroes it, so the cart shows the original price struck
 * through to €0.
 *
 * The discount engine settles asynchronously and re-evaluates at checkout, so
 * the flow is: add the gift silently → verify Shopify actually priced it €0
 * (with a short retry for the async window) → reveal it if free, otherwise
 * remove it so the customer is never charged.
 *
 * If a verify fails (discount lapsed/absent, or the product isn't addable) the
 * whole mechanism pauses until the next page load — so an inactive discount
 * doesn't keep firing add/remove calls on every cart change. It recovers by
 * itself on the next navigation once the discount is active again.
 *
 * The gift line carries a hidden `_free_gift` line-item property so it is never
 * confused with the same product bought at full price.
 */

const SOURCE = "free-gift";
const GIFT_PROPERTY = "_free_gift";
const SETTLE_MS = 500;

class FreeGift extends HTMLElement {
  /** @type {boolean} Guards against overlapping reconciles */
  #busy = false;
  /** @type {boolean} A reconcile was requested while busy */
  #pending = false;
  /** @type {boolean} Whether the gift was present (and free) last time we saw it */
  #hadGift = false;
  /** @type {boolean} A verify failed this page load (discount off / not addable) — stop probing until reload */
  #discountOff = false;
  /** @type {number | null} Non-gift subtotal at which the customer manually removed the gift; cleared when the subtotal changes */
  #dismissedAtSubtotal = null;
  /** @type {(() => void) & { cancel(): void }} */
  #debouncedReconcile;

  connectedCallback() {
    this.#debouncedReconcile = debounce(() => this.#reconcile(), 150);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // Handle carts that already qualify on page load.
    this.#reconcile();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#debouncedReconcile?.cancel();
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    // Ignore the cart updates we dispatch ourselves, to avoid a feedback loop.
    if (event?.detail?.data?.source === SOURCE) return;
    this.#debouncedReconcile();
  };

  get #giftVariantId() {
    return Number(this.dataset.variantId);
  }

  /** Threshold in the active presentment currency (cents). */
  get #thresholdCents() {
    const base = Number(this.dataset.threshold) || 0; // shop-currency cents
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    return Math.round(base * rate);
  }

  async #reconcile() {
    if (!this.#giftVariantId) return;
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;
    try {
      await this.#run();
    } catch (error) {
      console.error("[free-gift]", error);
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#reconcile();
      }
    }
  }

  async #run() {
    const variantId = this.#giftVariantId;
    const cart = await this.#fetchCart();
    const gift = this.#findGift(cart, variantId);
    const subtotal = this.#nonGiftSubtotal(cart, variantId);
    const qualifies = subtotal >= this.#thresholdCents;

    // --- Below threshold: no gift allowed ---
    if (!qualifies) {
      this.#dismissedAtSubtotal = null;
      if (gift) await this.#removeGift(gift.key, true);
      this.#hadGift = false;
      return;
    }

    // --- A gift line is already in the cart ---
    if (gift) {
      if (this.#isFree(gift)) {
        this.#hadGift = true;
        return;
      }
      // Present but not free — the discount may still be settling. Re-check once.
      await this.#delay(SETTLE_MS);
      const recheck = this.#findGift(await this.#fetchCart(), variantId);
      if (recheck && this.#isFree(recheck)) {
        this.#hadGift = true;
      } else if (recheck) {
        console.warn("[free-gift] gift not made free by the discount — pausing until reload", this.#priceInfo(recheck));
        await this.#removeGift(recheck.key, true);
        this.#discountOff = true;
        this.#hadGift = false;
      }
      return;
    }

    // --- No gift, cart qualifies ---
    // The customer removed it themselves → respect it until the cart changes.
    if (this.#hadGift) {
      this.#dismissedAtSubtotal = subtotal;
    }
    this.#hadGift = false;

    // Stop entirely if a verify already failed this page load, or the customer
    // dismissed the gift at this subtotal.
    if (this.#discountOff || this.#dismissedAtSubtotal === subtotal) return;

    // Add silently, then let the discount decide (with a retry for the async window).
    await this.#addGift();
    let added = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      added = this.#findGift(await this.#fetchCart(), variantId);
      if (!added || this.#isFree(added)) break;
      if (attempt === 0) await this.#delay(SETTLE_MS);
    }

    if (added && this.#isFree(added)) {
      await this.#renderCartSections();
      this.#hadGift = true;
    } else if (added) {
      // Not free (discount inactive) → remove silently and stop probing until reload.
      console.warn("[free-gift] gift not made free by the discount — pausing until reload", this.#priceInfo(added));
      await this.#removeGift(added.key, false);
      this.#discountOff = true;
    } else {
      // Add failed (e.g. out of stock) → stop probing until reload.
      this.#discountOff = true;
    }
  }

  /**
   * @param {object} cart
   * @param {number} variantId
   */
  #findGift(cart, variantId) {
    return cart.items.find((item) => this.#isGiftLine(item, variantId));
  }

  /**
   * Subtotal of everything except the free gift itself.
   * @param {object} cart
   * @param {number} variantId
   */
  #nonGiftSubtotal(cart, variantId) {
    return cart.items
      .filter((item) => !this.#isGiftLine(item, variantId))
      .reduce((sum, item) => sum + item.final_line_price, 0);
  }

  /**
   * @param {object} item
   * @param {number} variantId
   */
  #isGiftLine(item, variantId) {
    return Number(item.variant_id) === variantId && item.properties?.[GIFT_PROPERTY] === "true";
  }

  /**
   * Whether Shopify made the line fully free — final price €0, or the discount
   * allocations cover the whole original price.
   * @param {object} line
   */
  #isFree(line) {
    if (!line) return false;
    if (line.final_line_price === 0) return true;
    const allocated = (line.discount_allocations || []).reduce(
      (sum, allocation) => sum + Number(allocation.amount || 0),
      0
    );
    return line.original_line_price > 0 && allocated >= line.original_line_price;
  }

  /** @param {object} line */
  #priceInfo(line) {
    return {
      title: line.title,
      original_line_price: line.original_line_price,
      final_line_price: line.final_line_price,
      discount_allocations: line.discount_allocations,
    };
  }

  async #fetchCart() {
    const res = await fetch(FoxTheme.routes.cart);
    return res.json();
  }

  /** @param {number} ms */
  #delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Section ids of the rendered cart-items components (drawer + cart page). */
  #sectionIds() {
    const ids = [];
    document.querySelectorAll("cart-items-component").forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  #sectionPayload() {
    const ids = this.#sectionIds();
    return ids.length ? { sections: ids.join(","), sections_url: window.location.pathname } : {};
  }

  async #addGift() {
    const body = JSON.stringify({
      items: [
        {
          id: this.#giftVariantId,
          quantity: 1,
          properties: { [GIFT_PROPERTY]: "true" },
        },
      ],
    });

    const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
    const parsed = await res.json();

    if (parsed.status && parsed.message) {
      console.error("[free-gift]", parsed.message);
    }
  }

  /**
   * @param {string} key
   * @param {boolean} render Whether to re-render the cart (only needed when the gift was visible)
   */
  async #removeGift(key, render) {
    const body = JSON.stringify({
      id: key,
      quantity: 0,
      ...(render ? this.#sectionPayload() : {}),
    });
    const res = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", { body }));
    const parsed = await res.json();
    if (render) await this.#applyResponse(parsed);
  }

  /**
   * Re-render the current cart sections via the Section Rendering API, without
   * mutating the cart. Used to reveal the gift once it's confirmed free.
   */
  async #renderCartSections() {
    const ids = this.#sectionIds();
    if (!ids.length) return;
    const url = `${window.location.pathname}?sections=${ids.join(",")}`;
    const res = await fetch(url);
    const sections = await res.json();
    await this.#applyResponse({ sections });
  }

  /**
   * Morph the returned cart sections and notify the rest of the theme (cart
   * count, drawer, free-shipping goal, …).
   * @param {object} parsed
   */
  async #applyResponse(parsed) {
    if (!parsed || !parsed.sections) return;

    const sections = parsed.sections;
    let itemCount = 0;

    for (const sectionId of Object.keys(sections)) {
      await morphSection(sectionId, sections[sectionId]);
      const doc = new DOMParser().parseFromString(sections[sectionId], "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) itemCount = parseInt(countEl.textContent, 10) || itemCount;
    }

    document.querySelectorAll("cart-items-component").forEach((comp) => {
      const sid = comp.dataset.sectionId;
      if (sid && sections[sid]) {
        comp.dispatchEvent(new CartUpdateEvent({}, sid, { itemCount, source: SOURCE, sections }));
      }
    });
  }
}

if (!customElements.get("free-gift")) {
  customElements.define("free-gift", FreeGift);
}
